import { __read } from 'tslib';
import React, { useRef, useState, useMemo, useCallback, useLayoutEffect, useEffect } from 'react';
import cuid from 'cuid';
import useDebouncedCallback from '../../node_modules/use-debounce/esm/useDebouncedCallback.js';
import { useMachine } from '@xstate/react';
import { useDetectInputMethod } from '../../hooks/useDetectInputMethod.js';
import { treeStateMachine } from '../TreeMachine/treeStateMachine.js';
import { traverse } from '../TreeMachine/actions.js';
import { buttonSizeMap } from '../TreeCardContent.js';

var useTree = function (props) {
    var id = props.id, data = props.data, _a = props.rootListRef, rootListRef = _a === void 0 ? useRef(null) : _a, nodeAction = props.nodeAction, hideDescription = props.hideDescription, _b = props.size, size = _b === void 0 ? "md" : _b;
    var _c = __read(useState(id || cuid), 1), treeId = _c[0];
    var _d = __read(useState(false), 2), resizeTrigger = _d[0], setResizeTrigger = _d[1];
    var isUsingKeyboard = useDetectInputMethod().isUsingKeyboard;
    var treeNodeIds = useMemo(function () {
        var nodeIds = [];
        traverse(data, {
            nodeFunc: function (node) {
                nodeIds.push(node.id);
            },
        });
        return nodeIds;
    }, [data]);
    var listItemRefs = useMemo(function () { return treeNodeIds.map(function () { return React.createRef(); }); }, [treeNodeIds]);
    var getActiveRefIndexFromId = useCallback(function (nodeId) {
        return treeNodeIds.findIndex(function (treeNodeId) { return treeNodeId === nodeId; });
    }, [treeNodeIds]);
    /*
      Immer's AutoFreeze prevents React from being able to update refs.
      If it turns out that we don't need access to the Dom elements from the state machine
      then we could move them out into our React context so this isn't an issue.
  
      Cannot assign to read only property 'current' of object '#<Object>'
      https://github.com/immerjs/immer/issues/545#issuecomment-590615560
  
      Immer will not - re-freeze objects, so if we shallow freeze an object containing
      our refs then Immer will not freeze our refs.
    */
    var context = {
        treeId: treeId,
        nodes: data,
        refs: Object.freeze({ listRef: rootListRef }),
    };
    var _e = __read(useMachine(treeStateMachine, { context: context }), 2), machineState = _e[0], sendAction = _e[1];
    var _f = machineState.context, nodes = _f.nodes, activeNode = _f.activeNode, listRef = _f.refs.listRef, activeParentNode = _f.activeParentNode, nodeOrder = _f.nodeOrder;
    var onFocus = function (event) {
        var _a, _b;
        if (((_a = event.currentTarget) === null || _a === void 0 ? void 0 : _a.nodeName) === "LI" &&
            ((_b = listRef === null || listRef === void 0 ? void 0 : listRef.current) === null || _b === void 0 ? void 0 : _b.contains(event.target))) {
            sendAction(isUsingKeyboard() ? "KEYBOARD_FOCUS" : "FOCUS");
        }
    };
    var isTreeActive = machineState.matches("active");
    var handleTreeBlur = useCallback(function (e) {
        var _a;
        if (isTreeActive && !((_a = listRef === null || listRef === void 0 ? void 0 : listRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target))) {
            sendAction("BLUR");
        }
    }, [isTreeActive]);
    var setNodes = function (newNodes) {
        sendAction({ type: "setNodes", nodes: newNodes });
    };
    var debouncedHandleResize = useDebouncedCallback(function () {
        setResizeTrigger(function (prev) { return !prev; });
    }, 100);
    useLayoutEffect(function () {
        setNodes(data);
    }, [data]);
    useEffect(function () {
        var _a;
        if (isTreeActive && activeNode) {
            (_a = listItemRefs[(activeNode === null || activeNode === void 0 ? void 0 : activeNode.id) ? getActiveRefIndexFromId(activeNode === null || activeNode === void 0 ? void 0 : activeNode.id) : -1].current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }, [activeNode === null || activeNode === void 0 ? void 0 : activeNode.id, isTreeActive]);
    useEffect(function () {
        window.addEventListener("focusin", handleTreeBlur);
        return function () {
            window.addEventListener("focusin", handleTreeBlur);
        };
    }, [handleTreeBlur]);
    useEffect(function () {
        window.addEventListener("resize", debouncedHandleResize);
        return function () {
            window.removeEventListener("resize", debouncedHandleResize);
        };
    }, []);
    useEffect(function () {
        document.addEventListener("mousedown", handleTreeBlur);
        return function () {
            document.removeEventListener("mousedown", handleTreeBlur);
        };
    }, [handleTreeBlur]);
    var keyDownHandler = function (event) {
        if (isTreeActive) {
            var key = event.key;
            switch (key) {
                case "ArrowDown":
                case "ArrowUp":
                case "ArrowRight":
                case "ArrowLeft":
                case "End":
                case "Enter":
                case "Home":
                    event.preventDefault();
                    sendAction("keyDown".concat(key));
                    break;
                case "Escape":
                    event.preventDefault();
                    sendAction("keyDownEscape");
                    break;
                case " ":
                    event.preventDefault();
                    sendAction("keyDownSpace");
                    break;
            }
        }
    };
    var handleMouseEvent = function (event) {
        event.preventDefault();
        sendAction(event);
    };
    var events = {
        onClick: handleMouseEvent,
    };
    var getActionButtonProps = function (tabIndex) { return ({
        tabIndex: tabIndex,
        look: "naked",
        size: buttonSizeMap[size],
        sx: {
            alignSelf: "center",
        },
        onClick: function (e) {
            e.stopPropagation();
        },
    }); };
    return {
        activeNode: activeNode,
        activeParentNode: activeParentNode,
        events: events,
        getActionButtonProps: getActionButtonProps,
        hideDescription: hideDescription,
        isTreeActive: isTreeActive,
        keyDownHandler: keyDownHandler,
        listRef: listRef,
        nodeAction: nodeAction,
        nodeOrder: nodeOrder,
        nodes: nodes,
        onFocus: onFocus,
        resizeTrigger: resizeTrigger,
        setNodes: setNodes,
        size: size,
        listItemRefs: listItemRefs,
        getActiveRefIndexFromId: getActiveRefIndexFromId,
    };
};

export { useTree };
//# sourceMappingURL=useTree.js.map
